"use client";

import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getAccessToken, getRefreshToken, clearTokens } from "@/lib/auth";
import { supabase } from "@/lib/supabase";

const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";

interface AuthContextValue {
  isAuthenticated: boolean;
  token: string | null;
  refreshToken: string | null;
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  token: null,
  refreshToken: null,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthContextValue>({
    isAuthenticated: false,
    token: null,
    refreshToken: null,
  });

  // Read token from localStorage on mount
  const readToken = useCallback((): AuthContextValue => {
    const token = getAccessToken();
    const refreshToken = getRefreshToken();
    return {
      isAuthenticated: !!token,
      token,
      refreshToken,
    };
  }, []);

  // Resync from Supabase's own localStorage entry. Reading localStorage
  // directly (rather than `supabase.auth.getSession()`) avoids a race with
  // the landing's lazy/singleton Supabase client, which can hold a stale
  // in-memory `null` session after the React app logs in on a sibling route.
  // The source of truth is whatever the most recent login wrote to
  // `sb-*-auth-token`; we mirror its access_token and refresh_token into our
  // own keys so existing `isAuthenticated()` (sync) consumers keep working.
  //
  // URL-token preservation is NOT handled here. The pre-hydration script in
  // app/layout.tsx restores any ?token= from the URL into access_token AND
  // strips the param before React mounts, so by the time this runs, a
  // "url-restored" token looks identical to any other access_token in storage.
  const syncAuthState = useCallback(() => {
    try {
      const sbKey = Object.keys(localStorage).find(
        (k) => k.startsWith("sb-") && k.endsWith("-auth-token")
      );
      const raw = sbKey ? localStorage.getItem(sbKey) : null;
      const parsed = raw ? JSON.parse(raw) : null;
      const sbAccessToken: string | null =
        parsed?.access_token ?? parsed?.currentSession?.access_token ?? null;
      const sbRefreshToken: string | null =
        parsed?.refresh_token ?? parsed?.currentSession?.refresh_token ?? null;

      if (sbAccessToken) {
        if (localStorage.getItem(ACCESS_TOKEN_KEY) !== sbAccessToken) {
          localStorage.setItem(ACCESS_TOKEN_KEY, sbAccessToken);
        }
        if (sbRefreshToken && localStorage.getItem(REFRESH_TOKEN_KEY) !== sbRefreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, sbRefreshToken);
        }
      }
      // Note: we do NOT clear access_token when sbKey exists but has no
      // session. Pre-hydration already cleared stale tokens; clearing here
      // would also nuke URL-restored tokens that Supabase doesn't know about
      // yet (AuthInit syncs them in async).
    } catch {
      // malformed JSON — leave existing access_token and refresh_token
    }
    setAuthState(readToken());
  }, [readToken]);

  useEffect(() => {
    // Pre-hydration script already restored tokens from URL, cleaned the URL,
    // and cleared stale tokens. AuthInit will handle Supabase sync async.
    // Defer the initial sync so the effect subscribes first and avoids a
    // synchronous state update during mount.
    const initialSyncId = window.setTimeout(syncAuthState, 0);

    // Listen for auth state changes dispatched by AuthInit
    const handleAuthChange = () => {
      setAuthState(readToken());
    };

    window.addEventListener("auth:state-change", handleAuthChange);

    // pageshow fires on back/forward navigation (incl. bfcache restores).
    // Re-query Supabase because the session may have been written/cleared by
    // another route on this origin (e.g. the React app at /campaigns) while
    // this page was suspended. Re-reading only localStorage is not enough —
    // the landing's Supabase client caches an in-memory session that may be
    // stale relative to what's on disk.
    const handlePageShow = (e: PageTransitionEvent) => {
      // On bfcache restore, React does NOT re-run useEffect. Force reload so
      // the landing fully re-hydrates against current localStorage (the React
      // app may have written/cleared the Supabase session while suspended).
      if (e.persisted) {
        window.location.reload();
        return;
      }
      syncAuthState();
    };
    window.addEventListener("pageshow", handlePageShow);

    // storage event: another tab or another app on this origin wrote/cleared
    // the Supabase session or the mirrored access_token.
    const handleStorage = (e: StorageEvent) => {
      if (
        e.key === null ||
        e.key === ACCESS_TOKEN_KEY ||
        e.key === REFRESH_TOKEN_KEY ||
        e.key.startsWith("sb-")
      ) {
        syncAuthState();
      }
    };
    window.addEventListener("storage", handleStorage);

    // visibilitychange: re-check when tab regains focus (catches writes made
    // while the tab was hidden, where storage events may not have fired).
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        syncAuthState();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    // Supabase cross-tab auth sync. Fires SIGNED_IN / TOKEN_REFRESHED when a
    // login happens in any tab sharing this Supabase client's storage, and
    // SIGNED_OUT on logout. Keep localStorage in lockstep so isAuthenticated()
    // (sync) stays correct for components that read it directly.
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session?.access_token) {
          localStorage.setItem(ACCESS_TOKEN_KEY, session.access_token);
          if (session?.refresh_token) {
            localStorage.setItem(REFRESH_TOKEN_KEY, session.refresh_token);
          }
        } else if (event === "SIGNED_OUT") {
          clearTokens();
        }
        setAuthState(readToken());
      }
    );

    return () => {
      window.clearTimeout(initialSyncId);
      window.removeEventListener("auth:state-change", handleAuthChange);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibility);
      authListener.subscription.unsubscribe();
    };
  }, [readToken, syncAuthState]);

  return (
    <AuthContext.Provider value={authState}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
